import { when } from '@execonline-inc/maybe-adapter';
import { always } from '@kofno/piper';
import { fromEmpty } from 'maybeasy';
import { equalArrays, exclude, include, includes } from '../../../../../../../../Collections';
import { CompetencyFilterOptionData } from '../../../../../Types';

export type CompetencyFilterOptions = SomeCompetencies | AllCompetencies | NoCompetencies;

export type SomeCompetencies = {
  kind: 'some-competencies';
  name: string;
  id: number;
  count: number;
};

export type AllCompetencies = {
  kind: 'all-competencies';
  name: 'All';
  id: 'All';
  count: number;
};

export type NoCompetencies = {
  kind: 'no-competencies';
  name: string;
  id: 'None';
  count: 'None';
};

export type CompetencyFilter = {
  competencies: ReadonlyArray<string>;
};

export interface Opened {
  kind: 'opened';
  filter: CompetencyFilter;
}

export interface Closed {
  kind: 'closed';
  filter: CompetencyFilter;
}

export type State = Opened | Closed;

export const opened = (state: Closed): Opened => {
  return {
    kind: 'opened',
    filter: { ...state.filter },
  };
};

export const blank = (filter: CompetencyFilter): State => ({
  kind: 'closed',
  filter,
});

export const closed = (state: Opened): Closed => ({
  kind: 'closed',
  filter: { ...state.filter },
});

const allCompetenciesFilter = (
  competencies: ReadonlyArray<string>,
  initialFilterValues: ReadonlyArray<string>,
): ReadonlyArray<string> =>
  when(equalArrays(competencies, initialFilterValues), true)
    .map<readonly string[]>(always([]))
    .getOrElseValue(initialFilterValues);

export const updatingCompetency = (
  state: Opened,
  filterValue: CompetencyFilterOptions,
  initialFilterValues: CompetencyFilter['competencies'],
): Opened => {
  const { competencies } = state.filter;
  switch (filterValue.kind) {
    case 'all-competencies':
      return {
        kind: 'opened',
        filter: {
          competencies: allCompetenciesFilter(competencies, initialFilterValues),
        },
      };
    case 'no-competencies':
    case 'some-competencies':
      const buildList = includes(filterValue.name, competencies)
        ? exclude(filterValue.name)
        : include(filterValue.name);

      return {
        kind: 'opened',
        filter: {
          competencies: buildList(competencies),
        },
      };
  }
};

export const allCompetencyOptions = (
  options: CompetencyFilterOptionData[],
): CompetencyFilterOptions[] => [
  {
    kind: 'all-competencies',
    name: 'All',
    id: 'All',
    count: options.length,
  },
  ...getCompetencyOptions(options),
];

const noCompetencies = (): CompetencyFilterOptions => ({
  kind: 'no-competencies',
  name: 'None',
  id: 'None',
  count: 'None',
});

const someCompetencies = (data: CompetencyFilterOptionData): CompetencyFilterOptions => ({
  kind: 'some-competencies',
  name: data.name,
  id: data.id,
  count: data.count,
});

const competencyFilterObject = (object: CompetencyFilterOptionData): CompetencyFilterOptions =>
  fromEmpty(object.name)
    .map(() => someCompetencies(object))
    .getOrElse(noCompetencies);

const getCompetencyOptions = (options: CompetencyFilterOptionData[]): CompetencyFilterOptions[] =>
  options.map(competencyFilterObject);
